import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BentoBrand,
  BentoContact,
  EmailSequenceMap,
  NewBentoContact,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { EmailSettings } from "schemas/email";
import { routes } from "schemas/routes";

import { getPageName } from "constants/trackingProps";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";

import MainDialogBody from "./MainDialogBody";
import MainDialogHeader from "./MainDialogHeader";
import ReviewBrands from "./ReviewBrands";
import SuccessScreen from "./SuccessScreen";
import TimeSelection from "./TimeSelection";
import {
  Day,
  convertTimeToUtc,
  getDateOptions,
  getDefaultHours,
  getFutureHours,
} from "./TimeSelection/helpers";
import { buildInitialState } from "./helpers";
import {
  BulkSendEmailSetting,
  BulkSendState,
  DialogScreen,
  EmailSequenceBe,
  SelectedContactMap,
} from "./schema";

interface Props {
  open: boolean;
  handleClose: () => void;
  resetSelectedBrands: (skippedBrandIds?: number[]) => void;
  presetEmailSequenceMap: EmailSequenceMap;
  presetSelectedContacts?: SelectedContactMap;
  collectionId?: number;
  removeFromBulkBrands: (bentoBrand: BentoBrand) => void;
}

export default function BulkSendDialog({
  open,
  handleClose,
  collectionId,
  resetSelectedBrands,
  presetEmailSequenceMap,
  presetSelectedContacts,
  removeFromBulkBrands,
}: Props) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg, emailSettings, setEmailSettings } = useContext(
    OrganizationUserContext,
  );
  const { setNeedsRefreshList } = useContext(SavedBrandCollectionsContext);
  const { refreshGamificationProfile } = useGamification();
  const timezone = emailSettings?.timezone || moment.tz.guess();

  const { getToken } = useAuth();
  const { setUpgradeDialogSource, subscription, setSubscription } =
    useContext(SubscriptionContext);
  const { emailHealth, setOpenIntegrationDialog, fetchIntegrationHealth } =
    useContext(UserIntegrationsContext);
  const { dateOptions, defaultOption } = getDateOptions(timezone);

  const [loading, setLoading] = useState(false);
  const [dialogLoading, setDialogLoading] = useState<boolean>(false);
  const [state, setState] = useState<BulkSendState>({
    emailSequences: [],
    fallbackTemplates: [],
  });
  const [allEmailSettings, setAllEmailSettings] = useState<EmailSettings[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<SelectedContactMap>(
    {},
  );
  const [noContactBrandIds, setNoContactBrandIds] = useState<number[]>([]);

  const [datePicked, setDatePicked] = useState<Day>(defaultOption);
  const [customDatePicked, setCustomDatePicked] = useState({
    start: "",
    end: "",
  });
  const validHours = getFutureHours(datePicked, timezone);
  const { defaultStart, defaultEnd } = getDefaultHours(validHours);
  const [timePicked, setTimePicked] = useState({
    start: defaultStart,
    end: defaultEnd,
  });
  const [screen, setScreen] = useState<DialogScreen>(
    DialogScreen.templates_and_time,
  );
  const navigate = useNavigate();

  const close = () => {
    const wasOnSuccess = screen === DialogScreen.success;
    const skippedIds = noContactBrandIds;
    handleClose();
    setState({ emailSequences: [], fallbackTemplates: [] });
    setSelectedContacts({});
    setNoContactBrandIds([]);
    setScreen(DialogScreen.templates_and_time);
    if (wasOnSuccess) {
      resetSelectedBrands(skippedIds);
      setNeedsRefreshList(true);
      refreshGamificationProfile(true);
    }
  };

  const goToScheduled = () => {
    close();
    navigate(`/${routes.inboxScheduled}`);
  };

  const totalBrands = state.emailSequences.length;

  const setTemplates = (newTemplates: Template[]) => {
    setState((prev) => ({ ...prev, fallbackTemplates: newTemplates }));
  };

  const removeBrand = (bentoBrand: BentoBrand) => {
    setState((prev) => ({
      ...prev,
      emailSequences: prev.emailSequences.filter(
        (es) => es.bentoBrandId !== bentoBrand.id,
      ),
    }));
    setSelectedContacts((prev) => {
      const next = { ...prev };
      delete next[bentoBrand.id];
      return next;
    });
    removeFromBulkBrands(bentoBrand);
  };

  const validateBeforeSend = (): boolean => {
    if (
      subscription?.remainingMessages &&
      totalBrands > subscription?.remainingMessages
    ) {
      setAlert(
        `It looks like you're trying to send ${totalBrands} messages, but your current plan allows only ${subscription?.remainingMessages} more this billing cycle. Please reduce the number of messages or upgrade your plan to send more.`,
        "warning",
      );
      setUpgradeDialogSource(UpgradeDialogSource.hits_bulk_send_limit);
      return false;
    }

    if (!emailHealth) {
      setOpenIntegrationDialog(true);
      return false;
    }

    if (
      datePicked === Day.customized &&
      !(customDatePicked?.end && customDatePicked?.start)
    ) {
      setAlert("Please pick a start and end date", "error");
      return false;
    }

    if (!(timePicked?.start && timePicked?.end)) {
      setAlert("Please pick a start and end time", "error");
      return false;
    }

    const indexOfStart = validHours?.findIndex((x) => x === timePicked?.start);
    const indexOfEnd = validHours?.findIndex((x) => x === timePicked?.end);
    if (datePicked === Day.today && indexOfStart >= indexOfEnd) {
      setAlert("Start time must be before end time", "error");
      return false;
    }

    if (!state.fallbackTemplates?.length || !state.fallbackTemplates[0]?.id) {
      setAlert("Please select at least one outreach template", "error");
      return false;
    }

    return true;
  };

  const goToReview = () => {
    if (!validateBeforeSend()) return;
    trackEvent("Bulk Send Review Opened", {
      "Brand Count": totalBrands,
      Source: getPageName(),
    });
    setScreen(DialogScreen.review_brands);
  };

  const startSend = async () => {
    if (!validateBeforeSend()) return;

    const { startDate, endDate, startTime, endTime } = convertTimeToUtc(
      datePicked,
      customDatePicked,
      timePicked,
      timezone,
    );

    const fallbackTemplateIds = state.fallbackTemplates
      .map((t) => t.id)
      .filter((id): id is number => id !== undefined);

    const skippedIdSet = new Set(noContactBrandIds);
    const sendableSequences = state.emailSequences.filter(
      (es) => !skippedIdSet.has(es.bentoBrandId),
    );

    const emailSequences: EmailSequenceBe[] = sendableSequences.map((es) => {
      const contact = selectedContacts[es.bentoBrandId];
      const sequence: EmailSequenceBe = {
        bentoBrandId: es.bentoBrandId,
        fallbackTemplateIds,
      };
      if (contact && "id" in contact && contact.id) {
        sequence.bentoContactId = contact.id;
      } else if (contact?.email) {
        sequence.newContact = {
          email: contact.email,
          name: contact.name,
          title: contact.title,
        };
      }
      return sequence;
    });

    const body = {
      startDate,
      endDate,
      startTime,
      endTime,
      emailSequences,
      collectionId,
      source: getPageName(),
    };

    try {
      setLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/bulk-schedule`,
        "POST",
        {},
        body,
      );
      setScreen(DialogScreen.success);
      setSubscription((prev) => {
        if (prev) {
          prev["remainingMessages"] = Math.max(
            (prev?.remainingMessages || 0) - sendableSequences.length,
            0,
          );
        }
        return prev;
      });
      updateEmailSettings(fallbackTemplateIds);
    } catch (error) {
      if (error?.message?.includes("Google integration")) {
        setOpenIntegrationDialog(true);
      } else {
        setErrorAlert(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateEmailSettings = async (templateIds: number[]) => {
    const body: BulkSendEmailSetting = {
      templateIds,
      type: "category",
      isUncategorized: true,
    };
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings/bulk-send`,
        "PUT",
        {},
        { emailSettings: [body], timezone },
      );
      setAllEmailSettings(res.emailSettings);
      const defaultSetting = res.emailSettings?.find(
        (es: EmailSettings) => es.isDefault,
      );
      if (defaultSetting) setEmailSettings(defaultSetting);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleBack = () => {
    if (screen === DialogScreen.review_brands) {
      setScreen(DialogScreen.templates_and_time);
    }
  };

  const handleContactSelected = (
    bentoBrandId: number,
    contact: BentoContact | NewBentoContact | null,
  ) => {
    setSelectedContacts((prev) => ({ ...prev, [bentoBrandId]: contact }));
  };

  const fetchEmailSettings = async () => {
    setDialogLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings`,
        "GET",
      );
      setAllEmailSettings(res.emailSettings);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setDialogLoading(false);
    }
  };

  useEffect(() => {
    if (emailHealth) {
      setOpenIntegrationDialog(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailHealth]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg.id);
      fetchEmailSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (!dialogLoading && open) {
      setState(buildInitialState(presetEmailSequenceMap));
      setSelectedContacts(presetSelectedContacts ?? {});
      setScreen(DialogScreen.templates_and_time);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dialogLoading]);

  const renderActions = () => {
    if (dialogLoading) return null;

    if (screen === DialogScreen.success) {
      return (
        <Button
          disableElevation
          size="small"
          variant="contained"
          onClick={goToScheduled}
        >
          View scheduled
        </Button>
      );
    }

    if (screen === DialogScreen.templates_and_time) {
      return (
        <Button
          disableElevation
          size="small"
          variant="contained"
          onClick={goToReview}
        >
          Review{" "}
          <Box
            sx={{ ml: 1 }}
            component="i"
            className="fa-regular fa-arrow-right"
          />
        </Button>
      );
    }

    return (
      <LoadingButton
        disableElevation
        size="small"
        variant="contained"
        loading={loading}
        onClick={startSend}
        disabled={totalBrands === 0}
      >
        Start Send{" "}
        <Box
          sx={{ ml: 1 }}
          component="i"
          className="fa-regular fa-paper-plane"
        />
      </LoadingButton>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={isMobileScreen}
      fullWidth
      maxWidth="md"
    >
      {dialogLoading ? (
        <DialogContent
          sx={{
            p: 5,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </DialogContent>
      ) : (
        <DialogContent>
          <MainDialogHeader
            screen={screen}
            totalBrands={totalBrands}
            handleBack={handleBack}
          />

          {screen === DialogScreen.templates_and_time && (
            <MainDialogBody
              state={state}
              setTemplates={setTemplates}
              allEmailSettings={allEmailSettings}
              renderTimePickedOption={() => (
                <TimeSelection
                  setDatePicked={setDatePicked}
                  setTimePicked={setTimePicked}
                  setCustomDatePicked={setCustomDatePicked}
                  timezone={timezone}
                  datePicked={datePicked}
                  timePicked={timePicked}
                  customDatePicked={customDatePicked}
                  dateOptions={dateOptions}
                  validHours={validHours}
                />
              )}
            />
          )}

          {screen === DialogScreen.review_brands && (
            <ReviewBrands
              emailSequences={state.emailSequences}
              selectedContacts={selectedContacts}
              onContactSelected={handleContactSelected}
              onRemoveBrand={removeBrand}
              onNoContactBrandIdsChange={setNoContactBrandIds}
            />
          )}

          {screen === DialogScreen.success && (
            <SuccessScreen onGoToScheduled={goToScheduled} />
          )}
        </DialogContent>
      )}

      <DialogActions>
        <Button onClick={close} size="small" color="secondary">
          {screen === DialogScreen.success ? "Close" : "Cancel"}
        </Button>

        {renderActions()}
      </DialogActions>
    </Dialog>
  );
}
